<template>
  <div :class="bem({theme})">
    <CartItemShimmer v-if="pending || !customisedProduct" />
    <div
      v-else
      :class="bem('wrapper')"
    >
      <aside
        :class="bem('image-wrapper')"
      >
        <component
          :is="url ? VisionLink : 'div'"
          :to="url"
          :class="{ 'vision-shimmer': !imageLoaded }"
        >
          <DynamicImg
            :sku="(props.cartItem.bundleSku || customisedProduct.sku).replace('c4f', 'c3f')"
            :height="800"
            :width="800"
            bg="f7f7fa"
            :view-angle="5"
            :class="bem('product-image')"
            @loaded="handleImageLoad"
          />
          <div
            v-if="cartItem.bundleQuantity > 1"
            :class="bem('quantity')"
          >
            {{ cartItem.bundleQuantity }}
          </div>
        </component>
      </aside>

      <div :class="bem('content')">
        <div>
          <div>
            <component
              :is="url ? VisionLink : 'div'"
              :to="url"
              :data-testId="`cart-item-heading-${cartItem.bundleBaseSku}`"
              display="block"
              :class="bem('head')"
            >
              <VisionHeading
                v-if="customisedProduct?.isPrescription"
                tag="h6"
                font-weight="light"
                colour-style="grey"
                :class="bem('title-type')"
              >
                Prescription
              </VisionHeading>
              <div :class="bem('title-wrapper')">
                <VisionHeading
                  tag="h2"
                  size="h5"
                  colour-style="gradient"
                  :class="bem('title')"
                >
                  <!-- eslint-disable vue/no-v-html -->
                  <span v-html="productName" />
                <!-- eslint-enable vue/no-v-html -->
                </VisionHeading>
                <span
                  v-if="props.cartItem.bundleCostDifference || 0 < 0"
                  :class="bem('original-price')"
                >
                  <s>
                    {{ $tca(props.cartItem.bundleOriginalCost) }}
                  </s>
                </span>
              </div>
              <VisionHeading
                v-if="customisedProduct.subTitle"
                tag="h4"
                size="h5"
                font-weight="light"
                colour-style="grey"
                :class="bem('sub-title')"
              >
                <!-- eslint-disable vue/no-v-html -->
                <span v-html="customisedProduct.subTitle" />
                <!-- eslint-enable vue/no-v-html -->
              </VisionHeading>
            </component>
          </div>

          <VisionTag
            size="small"
            :text="props.cartItem.bundleCost === 0 ? 'Free Gift' : $tca(props.cartItem.bundleCost)"
            :theme="calculatedTagTheme"
            :outlined="true"
          />
        </div>
        
        <div :class="bem('change')">
          <VisionLink
            v-if="hasChoiceOfGiftDiscounts"
            :line-link="true"
            :is-button="true"
            :theme="isLinkPromo ? 'black' : 'gradient'"
            @click="promoSelectorOpen = !promoSelectorOpen"
          >
            Change
          </VisionLink>
        </div>
      </div>

      <PromoSelectorModal
        v-if="hasChoiceOfGiftDiscounts && promoSelectorOpen"
        :promo-selector-open="promoSelectorOpen"
        :customised-product="customisedParentProduct"
        :pre-selected-discount-sku="props.cartItem.bundleSku"
        :primary-sku-added-to-cart="{
          sku: customisedParentProduct.sku,
          baseSku: customisedParentProduct.baseSKU
        }"
        :change-gift="true"
        @toggle-promo-selector="handleTogglePromoSelector($event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { VisionLink } from '#components'
import type { Bundle } from '~/assets/js/helpers/shopify'

interface Props {
  cartItem: {
    bundle: Bundle
    bundleItems: Array<object>
    bundleId: string
    bundleSku: string
    bundleBaseSku: string
    bundleCost: number
    bundleOriginalCost: number
    bundleCostDifference: number
    bundleQuantity: number
    bundleIsGiftCard: boolean
    bundleDisplayOrder: number
    bundleParentSku: string,
    bundleParentBaseSku: string,
    bundleUsesTheme: string
  }
  theme: string
}

const props = defineProps<Props>()
const bem = useBEM('CartDiscountedItem')

const customisedProduct = ref<any>(null)
const imageLoaded = ref<boolean>(false)
const pending = ref<boolean>(true)
const customisedParentProduct = ref<any>()
const promoSelectorOpen = ref<boolean>(false)
const discountsForProduct = ref()
const { $product, $sentry } = useNuxtApp()
const shopifyStore = useShopifyStore()

function handleImageLoad() {
  imageLoaded.value = true
}

const calculatedTagTheme = computed(() => {
  return (props.cartItem.bundleUsesTheme === 'promo') ? 'promo' : 'default'
})

const isLinkPromo = computed(() => {
  return (props.cartItem.bundleUsesTheme === 'promo')
})

async function handleTogglePromoSelector(open: boolean) {
  const { $track } = useNuxtApp()

  promoSelectorOpen.value = open

  if (!open) {
    await $track.giftSelector.closed()
  }
}

onMounted(async () => {
  if (props.cartItem.bundleIsGiftCard) {
    customisedProduct.value = await $product.getCustomisedProduct('egiftcard',
    {
      sku: 'egiftcard',
    })

    pending.value = false
  } else if (props.cartItem.bundleBaseSku) {
    customisedProduct.value = await $product.getCustomisedProduct(
      props.cartItem.bundleBaseSku,
      {
        sku: props.cartItem.bundleSku,
        lensTechSelected: true,
        isPrebuiltVariant: props.cartItem.bundle?.summary?.bundleIsPrebuiltVariant
      },
      true
    )

    if (props.cartItem.bundleParentBaseSku || props.cartItem.bundleParentSku) {
      customisedParentProduct.value = await $product.getCustomisedProduct(props.cartItem.bundleParentBaseSku, {
        sku: props.cartItem.bundleParentSku
      })
    }

    if(customisedParentProduct.value) {
      discountsForProduct.value = shopifyStore.getDiscountsFromProduct(customisedParentProduct.value)
    }

    pending.value = false
  } else {
    $sentry.captureException('Bundle SKU is missing. Cannot show cart item.')
  }
})

const url = computed(() => {
  if (!customisedProduct.value?.product?.hasPdp) return null

  const url = new URL(customisedProduct.value.url, window.location.origin)
  url.searchParams.set('rlt', '1')

  return url.toString()
})

const productName = computed(() => {
  if (
    customisedProduct.value?.isLimitedEdition &&
    customisedProduct.value?.product?.name &&
    customisedProduct.value?.sku.startsWith('microfib')
  )
    return customisedProduct.value.product.name

  if (
    customisedProduct.value.isLimitedEdition &&
    !customisedProduct.value.product.useBaseProductName
  )
    return customisedProduct.value.product.name

  return customisedProduct.value.isLimitedEdition &&
    customisedProduct.value.product?.baseProduct?.name
    ? customisedProduct.value.product.baseProduct.name
    : customisedProduct.value.product.name
})

const hasChoiceOfGiftDiscounts = computed(() => {
  if (customisedParentProduct.value?.product?.promotionProductGroup?.giftedProduct?.length) {
    if (!discountsForProduct?.value?.anyDiscountIsFree && discountsForProduct?.value?.allDiscountOptions?.length === 1)
      return true

    else if (discountsForProduct?.value?.allDiscountOptions?.length > 1)
      return true
  }

  return false
})

watch(() => props.cartItem.bundleParentSku, async () => {
  const { $product } = useNuxtApp()

  customisedParentProduct.value = await $product.getCustomisedProduct(props.cartItem.bundleParentBaseSku, {
    sku: props.cartItem.bundleParentSku
  })
})
</script>

<style lang="postcss" scoped>
.CartDiscountedItem {
  @apply pb-4 border-b border-grey-100;
}

.CartDiscountedItem__wrapper {
  @apply flex gap-x-2 items-center border border-transparent border-gradient-white rounded-lg p-1.5;
}

.CartDiscountedItem__image-wrapper {
  @apply relative
    w-24 md:w-lg flex-shrink-0 aspect-square rounded-lg overflow-hidden mr-2;
}

.CartDiscountedItem__quantity {
  @apply bg-white rounded-full absolute bottom-1.5 text-[10px] aspect-square right-1.5 w-4 flex items-center justify-center font-bold;
}

.CartDiscountedItem__product-image {
  @apply overflow-hidden;
}

.CartDiscountedItem__head {
  @apply mb-1;
}

.CartDiscountedItem__title-wrapper {
  @apply flex gap-1.5 heading-5 mb-1;
}

.CartDiscountedItem__content {
  @apply w-full flex items-center pr-3 sm:pr-4 py-1 gap-2 justify-between;
}

.CartDiscountedItem__change {
  @apply text-xs;
}

.CartDiscountedItem__original-price {
  @apply font-medium text-grey-500;
}

.CartDiscountedItem__sub-title {
  @apply text-xxs;
}

/* Promo Theme */
.CartDiscountedItem--theme-promo {
  .CartDiscountedItem__wrapper {
    @apply border border-transparent;
    background:
      linear-gradient(
      var(--promo-shine, theme('colors.white')),
      var(--promo-shine, theme('colors.white')))
      padding-box,
      linear-gradient(45deg,
      var(--promo-key, theme('colors.purple')),
      var(--promo-highlight, theme('colors.blue-light')))
      border-box;
  }

  .CartDiscountedItem__title {
    @apply text-[var(--promo-bg)];
    background-image: none;
    -webkit-text-fill-color: var(--promo-bg);
  }
}
</style>